import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import { cn } from "@/lib/utils/cn";
import { homePlayfair } from "@/components/fonts";

const leadTextStyles = `${homePlayfair.className} text-balance text-sm  sm:leading-8 leading-7 sm:text-base lg:text-lg lg:leading-9`;
const largeTextStyles = `${homePlayfair.className} text-base leading-7 lg:text-xl lg:leading-8`;
const mediumTextStyles = `${homePlayfair.className} text-sm leading-7 sm:text-base sm:leading-8`;
const smallTextStyles = "text-sm leading-5";
const captionTextStyles = "text-xs leading-6 sm:text-sm sm:leading-7";
const extraSmallTextStyles = "text-[11px] font-medium tracking-[0.14em]";

const textToneStyles = {
  default: "text-zinc-300/90",
  subtle: "text-zinc-200/90",
  strong: "text-zinc-100",
  muted: "text-zinc-300/80",
  soft: "text-zinc-400",
  gold: "text-ghost-gold",
  inherit: "text-inherit",
} as const;

const textAlignStyles = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

type TextBlockProps<T extends ElementType = "span"> = {
  content?: ReactNode;
  children?: ReactNode;
  as?: T;
  variant?:
    | "lead"
    | "large"
    | "medium"
    | "small"
    | "caption"
    | "extra small"
    | "extra-small";
  tone?:
    | "default"
    | "subtle"
    | "strong"
    | "muted"
    | "soft"
    | "gold"
    | "inherit";
  align?: "left" | "center" | "right";
  desktopSize?: "default" | "grow";
  italic?: boolean;
  className?: string;
  dropCap?: boolean;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "className" | "children">;

export default function TextBlock<T extends ElementType = "span">({
  content,
  children,
  as,
  variant = "medium",
  tone = "default",
  align = "left",
  desktopSize = "default",
  italic = false,
  className,
  dropCap = false,
  ...props
}: TextBlockProps<T>) {
  const Component = as || "span";
  const renderedContent = children ?? content;

  const variantStyles: Record<
    NonNullable<TextBlockProps["variant"]>,
    string
  > = {
    lead: leadTextStyles,
    large: largeTextStyles,
    medium: mediumTextStyles,
    small: smallTextStyles,
    caption: captionTextStyles,
    "extra small": extraSmallTextStyles,
    "extra-small": extraSmallTextStyles,
  };

  const desktopGrowthStyles =
    desktopSize === "grow"
      ? variant === "small"
        ? "lg:text-base lg:leading-7"
        : variant === "caption"
          ? "lg:text-base lg:leading-8"
          : undefined
      : undefined;

  let stylesToUse = cn(
    variantStyles[variant],
    textToneStyles[tone],
    textAlignStyles[align],
    desktopGrowthStyles,
    italic && "italic",
  );

  if (dropCap) {
    stylesToUse +=
      " first-letter:pr-2 first-letter:text-6xl first-letter:font-medium first-letter:text-ghost-red lg:leading-9";
  }

  return (
    <Component className={cn(stylesToUse, className)} {...props}>
      {renderedContent}
    </Component>
  );
}
